import {CreateObjectDTO, EditObjectDTO} from '../../../common/models/camera/CreateObjectDTO'
import {ListFacialRecognitionDTO} from '../../../common/models/facial-recognition/ListFacialRecognitionDTO'
import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult} from './_types'

const prefix = process.env.REACT_APP_OBJECTS_BASE_URL

export type ObjectIncludesType =
  | 'interestArea'
  | 'objectType'
  | 'interestArea.cameraConfiguration'
  | 'interestArea.cameraConfiguration.camera'
  | 'faceRecognition'
  | 'faceLists'
  | 'actions'

export type BarrierType = 'interest-area' | 'line-crossing' | 'speed'

export type FaceRecognition = {
  objectId: number
  blockUnknownFaces: boolean
}

export type CameraHasObjects = {
  id: number
  confidence: string
  type: BarrierType
  objectTypeId: number
  isActive: boolean
  threshold: number
  objectType: ObjectType
  actions: ResponseActionType[]
  interestAreaId?: number | null
  isObjectCounting?: boolean | null
  interestArea: InterestAreaCam
  faceRecognition?: FaceRecognition | null
  faceLists?: ListFacialRecognitionDTO[]
  speedObject?: SpeedObjectOnCamera
  epiDetectionObjectId?: number
  epiDetectionObject?: EpiDetectionsOptions
  vehicleDetectionObjectId?: number
  vehicleDetectionObject?: VehicleDetectionsOptions
  isObjectPermanency?: boolean
  maximumTime?: number
  objectsDetectionObject?: ObjectsDetectionsOptions
  animalDetectionObject?: AnimalsDetectionsOptions
}

type EpiDetectionsOptions = {
  id: number
  epis: Array<string>
}

type ObjectsDetectionsOptions = {
  id: number
  objects: Array<string>
}

type AnimalsDetectionsOptions = {
  id: number
  animals: Array<string>
}

type VehicleDetectionsOptions = {
  id: number
  vehicles: Array<string>
}

export type SpeedObjectOnCamera = {
  id: number
  cameraId: number
  distance: number
  speedLimit: number
}

export type InterestAreaCam = {
  id: number
  name: string
  cameraConfigurationId: number
  type: BarrierType
}

export type ObjectType = {
  id: number
  code: string
  name: string
  label: string
  resourceId: number
}

export type ResponseActionType = {
  id: number
  objectId: number
  outputIntegrationId: number | null | string
  licensePlateListId: number | null
  blockUnknown: number | null
  faceListId: number | null
  telegramChatId: number | null
  mobileDeviceId: number | null
  fetchVehicleInfoFromSinesp?: boolean
  fetchVehicleInfoFromFipe?: boolean
  checkPlateIsRobbed?: boolean
  dispatchToEmail?: string
  dispatchAllPlates?: boolean
  dispatchPlateToHarpia?: boolean
  dispatchPlateToSpia?: boolean
  dispatchPlateToSivana?: boolean
  integrationCode?: string
  actionColor?: string
  whatsappLink?: string
  dispatchPlateToAspide?: boolean
  sigmaValidationId?: number
  validationChatId?: number
  userIdHasNotification?: number
  dispatchPlateToMuralhaPaulista?: boolean
}

export type CameraObjectCreatedResponse = {
  confidence: number
  type: ActionType
  objectTypeId: number
  isActive: boolean
  threshold?: number
  interestAreaId?: number
  isObjectCounting: boolean | null
  id: number
  actions: ResponseActionType[]
}

export type CameraObject = {
  id: number
  label: string
  name: string
  resourceId: number
  code: string
}

export type ActionType = 'output-integration' | 'telegram' | 'lpr-integration' | 'push-notification' | 'check-plate-robbed' | 'whatsapp'

export type CreateActions = {
  type?: ActionType
  outputActionId?: number | string
  listIds?: number[]
  blockUnknownByClient?: boolean
  telegramChatId?: number
  dispatchAllPlates?: boolean
  fetchVehicleInfoFromSinesp?: boolean
  fetchVehicleInfoFromFipe?: boolean
  checkPlateIsRobbed?: boolean
  dispatchToEmail?: string
  dispatchPlateToHarpia?: boolean
  dispatchPlateToSpia?: boolean
  dispatchPlateToSivana?: boolean
  actionColor?: string
  integrationCode?: string
}

export type SpeedObjectType = {
  confidence: number
  distance: number
  cameraId: number
  coordinates: SpeedDetectionCoordinateDTO[]
  speedLimit: number
}

export type SpeedDetectionCoordinateDTO = {
  x: number
  y: number
  reference: number
}

export class ObjectEndpoint {
  async getAllObjectTypes(): Promise<AxiosResult<CameraObject[]>> {
    return await axios.get(`${prefix}/object-types`)
  }

  async putActions(id: number, body: CreateActions[]): Promise<AxiosResult<CreateActions[]>> {
    return await axios.put(`${prefix}/actions/${id}`, body)
  }

  async deleteAction(actionId: number): Promise<AxiosResult<void>> {
    return await axios.delete(`${prefix}/actions/${actionId}`)
  }

  async getAllObjectsOnCamera(params: {cameraId: number; includes?: Array<string>}): Promise<AxiosResult<CameraHasObjects[]>> {
    return await axios.get(`${prefix}/objects`, {
      params: prepareParams(params),
    })
  }

  async createAllTypesObjects(body: CreateObjectDTO): Promise<AxiosResult<CameraObjectCreatedResponse>> {
    return await axios.post(`${prefix}/objects`, body)
  }

  async updateAllTypesObjects(body: EditObjectDTO): Promise<AxiosResult<CameraObjectCreatedResponse>> {
    return await axios.patch(`${prefix}/objects`, body)
  }

  async deleteAllTypesObject(id: number): Promise<AxiosResult<void>> {
    return await axios.delete(`${prefix}/objects/${id}`)
  }
}
